/**
 * Rhythmz — Scene: Fractal Tunnel
 * ──────────────────────────────────
 * Infinite zoom through recursive geometric rings. 16 torus rings at
 * exponentially increasing scales, continuously zooming toward camera.
 * Colors shift hot→cool with depth. Beat pulse at tunnel center.
 *
 * Audio: Bass → zoom speed. Beat → center flash + ring ripple.
 */

import * as THREE from 'three/webgpu';
import type { SceneHandle } from './SceneManager';

type N = any;

export interface TunnelUniforms { uTime: N; uBass: N; uMid: N; uBeat: N; uTreble: N; uCol0: N; uCol1: N; uCol2: N; }

const RING_COUNT = 20;

export function createFractalTunnel(u: TunnelUniforms): SceneHandle {
  const group = new THREE.Group();

  // ── Rings ──
  const ringGeo = new THREE.TorusGeometry(0.5, 0.03, 8, 64);
  const ringMat = new THREE.MeshPhysicalNodeMaterial({ metalness: 0.7, roughness: 0.2, clearcoat: 0.3, emissive: new THREE.Color(1, 0.4, 0.8), emissiveIntensity: 1.5 });
  const rings = new THREE.InstancedMesh(ringGeo, ringMat, RING_COUNT);
  rings.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
  group.add(rings);

  const ringData = Array.from({ length: RING_COUNT }, (_, i) => ({
    z: -i * 0.6,
    scaleVal: 0.5 + (i / RING_COUNT) * 3,
    rotSpeed: 0.3 + Math.random() * 0.5,
    colorT: i / RING_COUNT,
  }));

  // ── Center glow ──
  const coreGeo = new THREE.IcosahedronGeometry(0.2, 2);
  const coreMat = new THREE.MeshPhysicalNodeMaterial({ metalness: 0, roughness: 0, emissive: new THREE.Color(0.9, 0.2, 0.8), emissiveIntensity: 4 });
  const core = new THREE.Mesh(coreGeo, coreMat);
  core.position.set(0, 0, -0.5);
  group.add(core);

  let t = 0;
  const pivot = new THREE.Object3D();

  const update = (dt: number): void => {
    t += dt;
    const bass = u.uBass.value ?? 0;
    const beat = u.uBeat.value ?? 0;
    const zoom = 1.5 + bass * 4;

    // Update ring positions
    for (let i = 0; i < RING_COUNT; i++) {
      ringData[i].z += zoom * dt;
      if (ringData[i].z > 2) ringData[i].z -= RING_COUNT * 0.6;

      const d = ringData[i];
      const s = d.scaleVal * (1 + (3 - d.z) * 0.3);
      pivot.position.set(0, 0, d.z);
      pivot.rotation.x = t * d.rotSpeed * 0.3;
      pivot.rotation.y = t * d.rotSpeed * 0.5;
      const beatRipple = beat * 0.15 * Math.sin(d.z * 3 + t * 5);
      pivot.scale.set(s + beatRipple, s + beatRipple, s + beatRipple);
      pivot.updateMatrix();
      rings.setMatrixAt(i, pivot.matrix);
    }
    rings.instanceMatrix.needsUpdate = true;

    // Core pulse
    core.scale.setScalar(1 + beat * 0.8);

    group.rotation.y = t * 0.05;
  };

  const dispose = (): void => {
    ringGeo.dispose(); ringMat.dispose();
    coreGeo.dispose(); coreMat.dispose();
    group.clear();
  };

  return { group, update, dispose, name: 'Fractal Tunnel' };
}